// noinspection JSUnresolvedFunction,JSUnresolvedVariable

import VueScript2 from 'vue-script2'
import store from '@/store/index'
import { AUTH_CURRENT_USER } from '@/store/auth/auth.types'

export default {}
export let map = null

/**
 * We re-declare the google.maps.ControlPosition.x constants
 * as it is sometimes required before the google maps library
 * is loaded.
 */
// export const TOP_LEFT = 1
// export const TOP_CENTER = 2
// export const TOP_RIGHT = 3
// export const LEFT_CENTER = 4
// export const LEFT_TOP = 5
export const LEFT_BOTTOM = 6
// export const RIGHT_TOP = 7
// export const RIGHT_CENTER = 8
// export const RIGHT_BOTTOM = 9
// export const BOTTOM_LEFT = 10
// export const BOTTOM_CENTER = 11
// export const BOTTOM_RIGHT = 12

/**
 * Asynchronously loads the Google maps library and initializes it.
 *
 * @param element   Object The DOM object in which to render the map
 * @param options   Object The google map specific options
 * @param libraries An array of Google map libraries to load
 *
 * @returns {Promise<any>}
 */
export const init = (
  element,
  options = {},
  libraries = ['places', 'drawing', 'geometry', 'visualization']
) => {
  options = Object.assign({ showTraffic: false }, options)

  return new Promise((resolve, reject) => {
    const user = store.getters[AUTH_CURRENT_USER]
    let googleMapsUrl

    const libs = (libraries.length === 0) ? '' : '&libraries=' + libraries.join(',')

    if (user.google.maps.client_id) {
      googleMapsUrl = user.google.maps.api_url + '?client=' + user.google.maps.client_id + libs
    } else {
      googleMapsUrl = user.google.maps.api_url + '?key=' + user.google.maps.api_key + libs
    }

    // We use the VueScript2 Vue library to dynamically load the Google maps API scripts.
    VueScript2.load(googleMapsUrl).then(() => {
      let myLatlng = new google.maps.LatLng(-33.963831, 18.551997)

      let mapOptions = Object.assign({
        zoom: 8,
        center: myLatlng,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        scrollwheel: true
      }, options)

      map = new google.maps.Map(element, mapOptions)

      // We store custom data in a property called _ws_. This helps us to
      // easily keep track of state across async callbacks and promises.
      map._ws_ = {
        options: mapOptions,
        directionsService: new google.maps.DirectionsService(),
        directionsRenderer: new google.maps.DirectionsRenderer(),
        trafficLayer: null,
        rulerLastPoint: null,
        rulerPoints: [],
        rulerDistances: [],
        rulerPolyline: null,
        rulerSecondaryPolyline: null,
        rulerActivePolyline: null,
        rulerPointsChangedListener: null,
        rulerClickListener: null,
        rulerMouseMoveListener: null,
        circleClickListener: null,
        circleMouseMoveListener: null
      }

      if (options.showTraffic) {
        showTraffic(map)
      }

      const streetView = map.getStreetView()
      streetView.setOptions({
        addressControl: true,
        addressControlOptions: {
          position: google.maps.ControlPosition.LEFT_BOTTOM
        }
      })

      map._ws_.directionsRenderer.setMap(map)
      resolve(map)
    }).catch(error => {
      reject(error)
    })
  })
}

/**
 * Return true if the user's location Web API is supported. Call
 * this before calling getUserLocation()
 *
 * @returns Boolean
 */
export const canGetUserLocation = () => {
  return ('geolocation' in navigator)
}

/**
 * Returns the user location by making use of the geolocation.getCurrentPosition
 * Web API. Make sure to call canGetUserLocation() before calling this
 * function. A promise is return containing an instance of Position, see:
 * - https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition
 * - https://developer.mozilla.org/en-US/docs/Web/API/Position
 *
 * If failed, a PositionError will be returned, see:
 * - https://developer.mozilla.org/en-US/docs/Web/API/PositionError
 *
 * @returns Promise
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(position => {
        resolve(position)
      }, error => {
        reject(error)
      })
    } else {
      reject(new Error('Geolocation Web API not supported'))
    }
  })
}

/**
 * Pans the map to a specified lat/lng coordinate.
 *
 * @param map Google maps object
 * @param lat Number
 * @param lng Number
 *
 * @returns map
 */
export const panToLatLng = (map, lat, lng) => {
  map.panTo(new google.maps.LatLng(lat, lng))
}

/**
 * Pans the map to a previously requested position from getUserLocation()
 *
 * @param map      Google maps object
 * @param position Position
 *
 * @returns map
 */
export const panToUserLocation = (map, position) => {
  const latLng = new google.maps.LatLng(position.coords.latitude, position.coords.longitude)

  map.panTo(latLng)
  map._ws_.userPosition = position
  map._ws_.userLatLng = latLng
}

/**
 * Pans the map to a marker's position
 *
 * @param map    Google maps object
 * @param marker google.maps.Marker|null
 */
export const panToMarker = (map, marker) => {
  if (marker !== null) {
    map.panTo(marker.getPosition())
  }
}

/**
 * Adds an InfoWindow to a marker with the given content.
 *
 * @param map     Google maps object
 * @param marker  google.maps.Marker
 * @param content String
 *
 * @returns google.maps.InfoWindow
 */
export const addMarkerInfoWindow = (map, marker, content) => {
  let infoWindow = new google.maps.InfoWindow()
  infoWindow.setContent(content)
  infoWindow.open(map, marker)
  return infoWindow
}

/**
 * Clears a previously added InfoWindow.
 *
 * @param infoWindow google.maps.InfoWindow
 */
export const clearInfoWindow = (infoWindow) => {
  if (infoWindow !== null) {
    infoWindow.setMap(null)
  }
}

/**
 * Pans the map to a place id and sets a marker at its location
 *
 * @param map      Google maps object
 * @param place_id String
 * @param panTo    Boolean
 */
export const addMarkerByPlaceId = (map, place_id, panTo = true) => {
  return new Promise((resolve, reject) => {
    let geocoder = new google.maps.Geocoder()

    geocoder.geocode({ 'placeId': place_id }, function (results, status) {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results[0]) {
          if (panTo) {
            map.setZoom(17)
            map.setCenter(results[0].geometry.location)
          }

          let marker = new google.maps.Marker({
            map: map,
            position: results[0].geometry.location
          })

          let infoWindow = new google.maps.InfoWindow()
          infoWindow.setContent(results[0].formatted_address)
          infoWindow.open(map, marker)

          resolve(marker)
        }
      } else {
        reject(new Error('Unable to geocode Place ID'))
      }
    })
  })
}

/**
 * Geocodes a location by latLng, the resulting address is passed
 * along to promise resolve routines.
 *
 * @param map    Google maps object
 * @param latLng google.maps.LatLng
 */
export const geocodeLocation = (map, latLng) => {
  return new Promise((resolve, reject) => {
    let geocoder = new google.maps.Geocoder()
    geocoder.geocode({ 'location': latLng }, function (results, status) {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results.length > 0) {
          resolve(results)
        }
      } else {
        reject(new Error('Unable to geocode location'))
      }
    })
  })
}

/**
 * Geocodes a location by address, the resulting address is passed
 * along to promise resolve routines
 *
 * @param map     Google maps object
 * @param address String
 */
export const geocodeAddress = (map, address) => {
  return new Promise((resolve, reject) => {
    let geocoder = new google.maps.Geocoder()
    geocoder.geocode({ 'address': address }, function (results, status) {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results.length > 0) {
          resolve(results)
        }
      } else {
        reject(new Error('Unable to geocode location'))
      }
    })
  })
}

/**
 * Pans the map to the lat/lng coordinate and sets a marker at the location
 *
 * @param map       Google maps object
 * @param latitude  Number
 * @param longitude Number
 * @param panTo     Boolean
 *
 * @param options   Object
 * @return google.maps.Marker
 */
export const addMarkerByLatLng = (map, latitude, longitude, panTo = true, options = {}) => {
  const latLng = new google.maps.LatLng(latitude, longitude)

  if (panTo) {
    map.panTo(latLng)
  }

  return new google.maps.Marker({
    map: map,
    position: latLng,
    ...options
  })
}

/**
 * Adds a click event listener on the specified map and
 * returns an instance of google.maps.MapsEventListener, see:
 * https://developers.google.com/maps/documentation/javascript/reference/event#MapsEventListener
 *
 * To remove the listener, keep the results of this
 * method call and then use the remove() method on it, for
 * example:
 * let listener = addMapMouseMoveEvent(map, () => {})
 * listener.remove()
 *
 * Google's examples shows that you can also remove listeners
 * using: google.maps.removeListener(map, listener) or
 * map.removeListener(listener) but as of writing this comment
 * it fails with an error and the listener is not removed. I
 * tried everything to fix the problem, but it persisted.
 *
 * @param map google.maps.Map
 * @param callback Function
 *
 * @return google.maps.MapsEventListener
 */
export const addMapClickEvent = (map, callback) => {
  return map.addListener('click', callback)
}

/**
 * Adds a right-click event listener on the specified map and
 * returns an instance of google.maps.MapsEventListener, see:
 * https://developers.google.com/maps/documentation/javascript/reference/event#MapsEventListener
 *
 * To remove the listener, keep the results of this
 * method call and then use the remove() method on it, for
 * example:
 * let listener = addMapMouseMoveEvent(map, () => {})
 * listener.remove()
 *
 * Google's examples shows that you can also remove listeners
 * using: google.maps.removeListener(map, listener) or
 * map.removeListener(listener) but as of writing this comment
 * it fails with an error and the listener is not removed. I
 * tried everything to fix the problem, but it persisted.
 *
 * @param map google.maps.Map
 * @param callback Function
 *
 * @return google.maps.MapsEventListener
 */
export const addMapRightClickEvent = (map, callback) => {
  return map.addListener('rightclick', callback)
}

/**
 * Adds a mouse move event listener on the specified map and
 * returns an instance of google.maps.MapsEventListener, see:
 * https://developers.google.com/maps/documentation/javascript/reference/event#MapsEventListener
 *
 * To remove the listener, keep the results of this
 * method call and then use the remove() method on it, for
 * example:
 * let listener = addMapMouseMoveEvent(map, () => {})
 * listener.remove()
 *
 * Google's examples shows that you can also remove listeners
 * using: google.maps.removeListener(map, listener) or
 * map.removeListener(listener) but as of writing this comment
 * it fails with an error and the listener is not removed. I
 * tried everything to fix the problem, but it persisted.
 *
 * @param map google.maps.Map
 * @param callback Function
 *
 * @return google.maps.MapsEventListener
 */
export const addMapMouseMoveEvent = (map, callback) => {
  return map.addListener('mousemove', callback)
}

/**
 * Adds an event to a marker for when the user have finished dragging it and
 * returns an instance of google.maps.MapsEventListener, see:
 * https://developers.google.com/maps/documentation/javascript/reference/event#MapsEventListener
 *
 * To remove the listener, keep the results of this
 * method call and then use the remove() method on it, for
 * example:
 * let listener = addMapMouseMoveEvent(map, () => {})
 * listener.remove()
 *
 * Google's examples shows that you can also remove listeners
 * using: google.maps.removeListener(map, listener) or
 * map.removeListener(listener) but as of writing this comment
 * it fails with an error and the listener is not removed. I
 * tried everything to fix the problem, but it persisted.
 *
 * @param marker google.maps.Marker
 * @param callback Function
 *
 * @return Event
 */
export const addMarkerDragEndEvent = (marker, callback) => {
  return google.maps.event.addListener(marker, 'dragend', callback)
}

/**
 * Clears previously created markers.
 *
 * @param markers Array
 */
export const clearMarkers = (markers) => {
  markers.forEach(marker => {
    marker.setMap(null)
  })
}

/**
 * Binds the Google Autocomplete API to an input and when
 * an address is picked, pan's the map to it and sets a marker.
 *
 * @param map     Google maps object
 * @param element Object The DOM object in which to render the autocomplete
 * @param options The marker options
 *
 * @returns google.maps.places.Autocomplete
 */
export const autoComplete = (map, element, options = {}) => {
  let autoComplete = new google.maps.places.Autocomplete(element, { types: ['geocode'] })

  // If we have the user's location and set the autocomplete
  // regional bounds.
  if (map._ws_.hasOwnProperty('userPosition')) {
    let geolocation = {
      lat: map._ws_.userPosition.coords.latitude,
      lng: map._ws_.userPosition.coords.longitude
    }

    let circle = new google.maps.Circle({
      center: geolocation,
      radius: map._ws_.userPosition.coords.accuracy
    })

    autoComplete.setBounds(circle.getBounds())
  }

  let markerOptions = Object.assign({
    draggable: true,
    title: 'Drag me!',
    onAutoComplete: null
  }, options)

  autoComplete._map = map
  autoComplete._element = element
  autoComplete._marker = null
  autoComplete._markerMoved = false
  autoComplete._place_id = null
  autoComplete._markerOptions = markerOptions
  autoComplete._infoWindow = null
  map._ws_.autoComplete = autoComplete

  // When the user selects an address from the dropdown, populate the address
  // fields in the form. Note that we don't use an arrow function so that
  // we can get the scope of the autoComplete object itself inside the
  // function.
  autoComplete.addListener('place_changed', function () {
    // We are in context/scope of an autoComplete object.
    let place = this.getPlace()

    if (place.length === 0 || typeof place.geometry === 'undefined') {
      return
    }

    if (this._markerOptions.onAutoComplete !== null) {
      this._markerOptions.onAutoComplete(place)
    } else {
      handlePlaceChanged(autoComplete._map, place)
    }
  })

  return autoComplete
}

/**
 * Returns the results of a previous autocomplete request made
 * by the user. Please note that if the user manually moved the
 * marker, that the place_id will be set to null as it may be
 * invalid otherwise.
 *
 * @param map
 *
 * @returns Object format: {address: '', place_id: 'abcdef'', latitude: -30.1234456, longitude: 24.02321}
 */
export const getAutoCompleteResults = (map) => {
  let result = {
    address: null,
    place_id: null,
    latitude: null,
    longitude: null
  }

  if (map._ws_.hasOwnProperty('autoComplete')) {
    result.address = map._ws_.autoComplete._element.value
    if (map._ws_.autoComplete._marker !== null) {
      result.latitude = map._ws_.autoComplete._marker.getPosition().lat()
      result.longitude = map._ws_.autoComplete._marker.getPosition().lng()
      if (!map._ws_.autoComplete._markerMoved) {
        result.place_id = map._ws_.autoComplete._place_id
      }
    }
  }

  return result
}

/**
 * Clears a previously created autocomplete marker.
 *
 * @param map
 */
export const clearAutoCompleteMarker = (map) => {
  if (map._ws_.hasOwnProperty('autoComplete')) {
    if (map._ws_.autoComplete._infoWindow !== null) {
      map._ws_.autoComplete._infoWindow.setMap(null)
      map._ws_.autoComplete._infoWindow = null
    }

    if (map._ws_.autoComplete._marker !== null) {
      map._ws_.autoComplete._marker.setMap(null)
      map._ws_.autoComplete._marker = null
    }
  }
}

/**
 * Called by an AutoComplete 'place_changed' event handler. Sets
 * the marker at the place's geometry location and also allows
 * the user to move the marker if allowed to.
 */
export const handlePlaceChanged = (map, place) => {
  let autoComplete = map._ws_.autoComplete
  map._ws_._place_id = place.place_id

  if (autoComplete._marker !== null) {
    autoComplete._marker.setPosition(place.geometry.location)
  } else {
    let markerOptions = Object.assign({
      position: place.geometry.location,
      map: map
    }, autoComplete._markerOptions)

    autoComplete._marker = new google.maps.Marker(markerOptions)

    if (autoComplete._markerOptions.draggable) {
      google.maps.event.addListener(map, 'click', function (event) {
        map._ws_.autoComplete._markerMoved = true
        map._ws_.autoComplete._place_id = null
        map._ws_.autoComplete._marker.setPosition(event.latLng)
      })
    }
  }

  google.maps.event.addListener(autoComplete._marker, 'click', function () {
    autoComplete._infoWindow.open(map, this)
  })

  let infoWindow
  if (autoComplete._infoWindow === null) {
    infoWindow = new google.maps.InfoWindow()
  } else {
    infoWindow = autoComplete._infoWindow
  }
  infoWindow.setContent(
    place.formatted_address + '<br>' + 'Lat/Lng: ' +
    place.geometry.location.lat().toFixed(6) + ' / ' +
    place.geometry.location.lng().toFixed(6)
  )
  infoWindow.open(map, autoComplete._marker)

  let bounds = new google.maps.LatLngBounds()

  if (place.geometry.viewport) {
    bounds.union(place.geometry.viewport)
  } else {
    bounds.extend(place.geometry.location)
  }

  autoComplete._element.value = place.formatted_address
  autoComplete._infoWindow = infoWindow
  map.fitBounds(bounds)
  map.setZoom(17)
}

/**
 * Request directions.
 */
export const getDirections = (map, request) => {
  return new Promise((resolve, reject) => {
    map._ws_.directionsService.route(request, function (response, status) {
      if (status === 'OK') {
        resolve(response)
      } else {
        reject(response)
      }
    })
  })
}

/**
 * Returns an error message for the given code.
 */
export const getDirectionsErrorMsg = (statusCode) => {
  switch (statusCode) {
    case 'OK':
      return ''

    case 'NOT_FOUND':
      return 'Either the source or destination could not be found.'

    case 'ZERO_RESULTS':
      return 'Unfortunately we could not calculate a route for you based on your preferences.'

    case 'MAX_WAYPOINTS_EXCEEDED':
      return 'Too many waypoints were provided for us to process and provide.'

    case 'MAX_ROUTE_LENGTH_EXCEEDED':
      return 'The route is unfortunately too long for us to process and provide.'

    case 'INVALID_REQUEST':
      return 'An invalid route request was provided. Please contact your service provider.'

    case 'OVER_QUERY_LIMIT':
      return 'Unfortunately too many requests have been sent from this web page.'

    case 'REQUEST_DENIED':
      return 'Unfortunately the request was denied by Google services.'

    default:
      return 'Unfortunately an unknown error occurred and we cannot process your request, please try again later.'
  }
}

/**
 * Render previously requested directions.
 *
 * @param map
 * @param response
 */
export const renderDirections = (map, response) => {
  map._ws_.directionsRenderer.setDirections(response)
}

/**
 * Render previously requested directions.
 *
 * @param map
 * @param panel
 */
export const setDirectionsPanel = (map, panel) => {
  map._ws_.directionsRenderer.setPanel(panel)
}

/**
 * Clear current directions from the map.
 *
 * @param map
 */
export const clearDirections = (map) => {
  map._ws_.directionsRenderer.setMap(null)
  map._ws_.directionsRenderer.setPanel(null)
  map._ws_.directionsRenderer = new google.maps.DirectionsRenderer()
  map._ws_.directionsRenderer.setMap(map)
}

/**
 * Show traffic on map.
 *
 * @param map
 *
 * @return google.maps.TrafficLayer
 */
export const showTraffic = (map) => {
  if (map._ws_.trafficLayer === null) {
    map._ws_.trafficLayer = new google.maps.TrafficLayer()
    map._ws_.trafficLayer.setMap(map)
  }

  return map._ws_.trafficLayer
}

/**
 * Hide traffic from map.
 *
 * @param map
 */
export const hideTraffic = (map) => {
  if (map._ws_.trafficLayer !== null) {
    map._ws_.trafficLayer.setMap(null)
    map._ws_.trafficLayer = null
  }
}

/**
 * Setup the map for ruler usage.
 *
 * @param map
 * @param onPointsChanged Function Event handler for when a point is added to the ruler
 */
export const startMeasurementRuler = (map, onPointsChanged = null) => {
  map.setOptions({
    draggableCursor: 'crosshair'
  })

  map._ws_.rulerPointsChangedListener = onPointsChanged

  map._ws_.rulerClickListener = google.maps.event.addListener(map, 'click', function (event) {
    map._ws_.rulerPoints.push(event.latLng)
    map._ws_.rulerLastPoint = event.latLng

    if (map._ws_.rulerPoints.length < 2) {
      // This is to allow us to render a dotted line
      const lineSymbol = {
        path: 'M 0,-1 0,1',
        strokeOpacity: 0.5,
        scale: 2
      }

      map._ws_.rulerActivePolyline = new google.maps.Polyline({
        path: map._ws_.rulerPoints,
        geodesic: false,
        strokeColor: '#FF0000',
        strokeWeight: 3,
        strokeOpacity: 0,
        clickable: false,
        icons: [{
          icon: lineSymbol,
          offset: '0',
          repeat: '10px'
        }]
      })
      map._ws_.rulerActivePolyline.setMap(map)

      map._ws_.rulerPolyline = new google.maps.Polyline({
        path: map._ws_.rulerPoints,
        geodesic: true,
        strokeColor: '#FF0000',
        strokeOpacity: 1.0,
        strokeWeight: 1,
        clickable: false
      })
      map._ws_.rulerPolyline.setMap(map)

      map._ws_.rulerSecondaryPolyline = new google.maps.Polyline({
        path: map._ws_.rulerPoints,
        geodesic: true,
        strokeColor: '#FF0000',
        strokeOpacity: 0.3,
        strokeWeight: 3,
        clickable: false
      })
      map._ws_.rulerSecondaryPolyline.setMap(map)
    } else {
      map._ws_.rulerPolyline.setPath(map._ws_.rulerPoints)
      map._ws_.rulerSecondaryPolyline.setPath(map._ws_.rulerPoints)
      map._ws_.rulerActivePolyline.setPath([map._ws_.rulerLastPoint])

      const pointCount = map._ws_.rulerPoints.length
      map._ws_.rulerDistances.push(calcDistanceKm(map._ws_.rulerPoints[pointCount - 2], event.latLng))
    }

    if (map._ws_.rulerPointsChangedListener !== null) {
      map._ws_.rulerPointsChangedListener(map._ws_.rulerPoints, map._ws_.rulerDistances)
    }
  })

  map._ws_.rulerMouseMoveListener = google.maps.event.addListener(map, 'mousemove', function (event) {
    if (map._ws_.rulerLastPoint !== null) {
      map._ws_.rulerActivePolyline.setPath([map._ws_.rulerLastPoint, event.latLng])
    }
  })
}

/**
 * Removes the measurement ruler tool.
 *
 * @param map
 */
export const removeMeasurementRuler = (map) => {
  map.setOptions({
    draggableCursor: 'grab'
  })

  google.maps.event.removeListener(map._ws_.rulerClickListener)
  google.maps.event.removeListener(map._ws_.rulerMouseMoveListener)

  clearMeasurementRuler(map)
}

/**
 * Clears the measurement ruler.
 *
 * @param map
 */
export const clearMeasurementRuler = (map) => {
  map._ws_.rulerPoints = []
  map._ws_.rulerDistances = []
  map._ws_.rulerLastPoint = null

  if (map._ws_.rulerActivePolyline !== null) {
    map._ws_.rulerActivePolyline.setMap(null)
    map._ws_.rulerActivePolyline = null
  }

  if (map._ws_.rulerPolyline !== null) {
    map._ws_.rulerPolyline.setMap(null)
    map._ws_.rulerPolyline = null
  }

  if (map._ws_.rulerSecondaryPolyline !== null) {
    map._ws_.rulerSecondaryPolyline.setMap(null)
    map._ws_.rulerSecondaryPolyline = null
  }

  if (map._ws_.rulerPointsChangedListener !== null) {
    map._ws_.rulerPointsChangedListener(map._ws_.rulerPoints, map._ws_.rulerDistances)
  }
}

/**
 * Removes the last measurement point.
 *
 * @param map
 */
export const removeLastRulerPoint = (map) => {
  if (map._ws_.rulerPoints.length > 0) {
    map._ws_.rulerPoints.pop()
  }

  if (map._ws_.rulerDistances.length > 0) {
    map._ws_.rulerDistances.pop()
  }

  map._ws_.rulerPolyline.setPath(map._ws_.rulerPoints)
  map._ws_.rulerSecondaryPolyline.setPath(map._ws_.rulerPoints)

  if (map._ws_.rulerPoints.length > 0) {
    map._ws_.rulerLastPoint = map._ws_.rulerPoints[map._ws_.rulerPoints.length - 1]
    map._ws_.rulerActivePolyline.setPath([map._ws_.rulerLastPoint])
  } else {
    map._ws_.rulerLastPoint = null
    map._ws_.rulerActivePolyline.setPath([])
  }

  if (map._ws_.rulerPointsChangedListener !== null) {
    map._ws_.rulerPointsChangedListener(map._ws_.rulerPoints, map._ws_.rulerDistances)
  }
}

/**
 * Optimized distance calculator between two google.maps.LatLng coordinates. Uses
 * the great circle distance or "Haversine" formula.
 *
 * @see https://stackoverflow.com/questions/27928/calculate-distance-between-two-latitude-longitude-points-haversine-formula
 *
 * @param from google.maps.LatLng
 * @param to google.maps.LatLng
 */
export const calcDistanceKm = (from, to) => {
  const p = 0.017453292519943295 // Math.PI / 180
  let a = 0.5 - Math.cos((to.lat() - from.lat()) * p) / 2 +
    Math.cos(from.lat() * p) * Math.cos(to.lat() * p) *
    (1 - Math.cos((to.lng() - from.lng()) * p)) / 2

  return 12742 * Math.asin(Math.sqrt(a)) // 2 * R; R = 6371 km
}

/**
 * Fit the map around the boundaries given, an
 * attempt is made to animate the transition since
 * Google's fitBounds() function does not animate.
 *
 * IMPORTANT: The animation is slow and jerky and
 * this should only be used as a last resort.
 *
 * @param bounds google.maps.LatLngBounds()
 */
export const fitBounds = (bounds) => {
  // Calculate the center-point of the boundaries.
  // const lat = bounds.
  map.panTo(bounds.getCenter())
  window.setTimeout(() => {
    map.fitBounds(bounds)
  }, 500)
}

/**
 * Returns the map's current zoom level. The zoom ranges are:
 * Regular Map: 0 (zoomed out) -> 22 (zoomed in)
 * Satellite Map: 0 (zoomed out) -> 19 (zoomed in)
 *
 * @returns int
 */
export const getZoom = () => {
  return map.getZoom()
}

/**
 * Adds a new circle to the map.
 *
 * @param options Object
 *
 * @returns google.maps.Circle
 */
export const addCircle = (options) => {
  return new google.maps.Circle({
    strokeColor: options.stroke_color,
    strokeOpacity: options.stroke_opacity,
    strokeWeight: options.stroke_weight,
    fillColor: options.fill_color,
    fillOpacity: options.fill_opacity,
    map,
    center: { lat: options.center.lat, lng: options.center.lng },
    radius: options.radius,
    clickable: options.clickable
  })
}

/**
 * Adds a new rectangle to the map.
 *
 * @param options Object
 *
 * @returns google.maps.Rectangle
 */
export const addRectangle = (options) => {
  return new google.maps.Rectangle({
    strokeColor: options.stroke_color,
    strokeOpacity: options.stroke_opacity,
    strokeWeight: options.stroke_weight,
    fillColor: options.fill_color,
    fillOpacity: options.fill_opacity,
    map,
    bounds: options.bounds,
    clickable: options.clickable
  })
}

/**
 * Adds a new polygon to the map.
 *
 * @param options Object
 *
 * @returns google.maps.Polygon
 */
export const addPolygon = (options) => {
  return new google.maps.Polygon({
    strokeColor: options.stroke_color,
    strokeOpacity: options.stroke_opacity,
    strokeWeight: options.stroke_weight,
    fillColor: options.fill_color,
    fillOpacity: options.fill_opacity,
    map,
    paths: options.paths,
    clickable: options.clickable
  })
}



// WEBPACK FOOTER //
// ./src/common/googleMaps.js